import React from 'react';

interface RecentActivityProps {
  dailySummary: any;
  userRole: string;
}

const RecentActivity: React.FC<RecentActivityProps> = ({ dailySummary, userRole }) => {
  const getActivitiesForRole = () => {
    const activities = [];

    if (userRole === 'manager') {
      activities.push(
        ...(dailySummary?.recent_activity || []).map((activity: any) => ({
          id: activity.id,
          type: 'system',
          title: activity.title || 'System Activity',
          description: activity.description || 'System operation completed',
          timestamp: activity.timestamp || new Date().toISOString(),
          status: activity.status || 'completed'
        }))
      );
    }

    if (userRole === 'worker') {
      activities.push(
        ...(dailySummary?.worker_activities || []).map((activity: any) => ({
          id: activity.id,
          type: 'order',
          title: `Order #${activity.order_number}`,
          description: `${activity.items_picked} items picked`,
          timestamp: activity.completed_at,
          status: activity.status
        }))
      );
    }

    // Fallback activities if no data
    if (activities.length === 0) {
      activities.push(
        {
          id: 1,
          type: 'info',
          title: 'Welcome to Dashboard',
          description: 'Start using the system to see recent activities here',
          timestamp: new Date().toISOString(),
          status: 'info'
        }
      );
    }

    return activities.slice(0, 5); // Show only last 5 activities
  };

  const activities = getActivitiesForRole();

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'order':
        return 'O';
      case 'inventory':
        return 'I';
      case 'user':
        return 'U';
      case 'system':
        return 'S';
      default:
        return 'i';
    }
  };

  const formatTimestamp = (timestamp: string) => {
    try {
      return new Date(timestamp).toLocaleString();
    } catch {
      return 'Unknown time';
    }
  };

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">Recent Activity</h3>
      </div>
      <div className="divide-y divide-gray-200">
        {activities.map((activity) => (
          <div key={activity.id} className="px-6 py-4 hover:bg-gray-50">
            <div className="flex items-start space-x-3">              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                  <span className="text-sm font-medium text-gray-600">
                    {getTypeIcon(activity.type)}
                  </span>
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {activity.title}
                  </p>
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(activity.status)}`}>
                    {activity.status}
                  </span>
                </div>
                <p className="text-sm text-gray-500 mt-1">{activity.description}</p>
                <p className="text-xs text-gray-400 mt-1">
                  {formatTimestamp(activity.timestamp)}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
      {activities.length === 0 && (
        <div className="px-6 py-8 text-center">
          <p className="text-gray-500">No recent activity</p>
        </div>
      )}
    </div>
  );
};

export default RecentActivity;
